// Creates a definition for the Divider component to be used in Contentful Studio
import {ComponentDefinition} from '@contentful/experiences-sdk-react';

import {
  BrandColor,
  brandColorOptionsWithDefault,
} from '@/components/common/colors';

// Brand options for the Divider dropdown. `primary` and `white` are filtered
// out because their value strings collide with the legacy Divider color
// values (also `primary` and `white`), which render via the existing
// class-based theme overrides. Authors who want a white divider should pick
// the "White (legacy)" option at the bottom of the list.
const dividerBrandOptions = (defaultValue: BrandColor) =>
  brandColorOptionsWithDefault(defaultValue).filter(
    opt => opt.value !== 'primary' && opt.value !== 'white',
  );

// Legacy Divider colors — render via the existing `.divider--color-{value}`
// CSS classes. Kept at the bottom of the list so existing Contentful entries
// continue to validate. `strong` displays as "Secondary (legacy)" — the
// stored value remains `strong` so existing usage on Code.org keeps working.
const DIVIDER_LEGACY_COLOR_OPTIONS = [
  {value: 'primary', displayName: 'Primary (legacy)'},
  {value: 'strong', displayName: 'Secondary (legacy)'},
  {value: 'white', displayName: 'White (legacy)'},
];

export const DividerContentfulComponentDefinition: ComponentDefinition = {
  id: 'divider',
  name: 'Divider',
  category: '02: Page Structure',
  // Adding an empty array here so no default style options show in the Design tab.
  builtInStyles: [],
  thumbnailUrl:
    'https://contentful-images.code.org/90t6bu6vlf76/6UpajalIAQ0bHw17sZky2Y/6c93c9859576d981676325338e844075/component_divider_thumbnail.png',
  tooltip: {
    description:
      'Use a divider to visually separate content sections. It spans the full width and helps improve readability and layout structure.',
    imageUrl:
      'https://contentful-images.code.org/90t6bu6vlf76/3gRz7bA5miAVaFwJqM6w18/075ca1479e4c79c3969e3cb4a87a9992/component_divider_tooltip.png',
  },
  variables: {
    color: {
      displayName: 'Color',
      type: 'Text',
      defaultValue: 'purplePrimary',
      group: 'style',
      validations: {
        in: [
          ...dividerBrandOptions('purplePrimary'),
          ...DIVIDER_LEGACY_COLOR_OPTIONS,
        ],
      },
    },
    margin: {
      displayName: 'Margin',
      type: 'Text',
      defaultValue: 'm',
      group: 'style',
      validations: {
        in: [
          {value: 'none', displayName: 'None'},
          {value: 'xs', displayName: 'Extra Small'},
          {value: 's', displayName: 'Small'},
          {value: 'm', displayName: 'Medium'},
          {value: 'l', displayName: 'Large'},
        ],
      },
    },
  },
};

// Code.org-only variant: adds Direction and Width options and defaults the
// color to Gray 3. CSforAll keeps the base definition above, so these options
// never appear there. Same `id` — only one definition is registered per brand.
export const CodeOrgDividerContentfulComponentDefinition: ComponentDefinition =
  {
    ...DividerContentfulComponentDefinition,
    tooltip: {
      ...DividerContentfulComponentDefinition.tooltip,
      description:
        'Use a divider to visually separate content sections. Horizontal dividers span the full width; vertical dividers stretch to the height of a row layout (e.g. between columns).',
    },
    variables: {
      ...DividerContentfulComponentDefinition.variables,
      color: {
        ...DividerContentfulComponentDefinition.variables.color,
        defaultValue: 'gray3',
        validations: {
          in: [
            ...dividerBrandOptions('gray3'),
            ...DIVIDER_LEGACY_COLOR_OPTIONS,
          ],
        },
      },
      direction: {
        displayName: 'Direction',
        type: 'Text',
        defaultValue: 'horizontal',
        group: 'style',
        validations: {
          in: [
            {value: 'horizontal', displayName: 'Horizontal'},
            {value: 'vertical', displayName: 'Vertical'},
          ],
        },
      },
      width: {
        displayName: 'Width',
        type: 'Text',
        defaultValue: 'small',
        group: 'style',
        validations: {
          in: [
            {value: 'small', displayName: 'Small'},
            {value: 'medium', displayName: 'Medium'},
          ],
        },
      },
    },
  };
